// Visual primitives. Each `Section` is its own self-contained card on the
// page — never wrap them inside another card. The colored left accent is
// part of the same border-box so it naturally tracks the section's height.

import { useState } from 'react';
import { Copy, Check } from 'lucide-react';

export type Accent = 'blue' | 'green' | 'amber' | 'rose' | 'slate';
export type Tone = 'ok' | 'warn' | 'bad' | 'info' | 'mute';

export function PageHeader({
  title, subtitle, right
}: {
  title: string;
  subtitle?: string;
  right?: React.ReactNode;
}) {
  return (
    <header className="flex flex-wrap items-end justify-between gap-3 pb-2">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight text-ink">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-ink-3">{subtitle}</p>}
      </div>
      {right && <div>{right}</div>}
    </header>
  );
}

export function Section({
  title, status, accent = 'blue', children, dense = false
}: {
  title?: string;
  status?: { label: string; tone: Tone };
  accent?: Accent;
  children: React.ReactNode;
  dense?: boolean;
}) {
  const accentClass = {
    blue:  'panel-accent-blue',
    green: 'panel-accent-green',
    amber: 'panel-accent-amber',
    rose:  'panel-accent-rose',
    slate: 'panel-accent-slate'
  }[accent];

  const pillToneClass = status && {
    ok: 'pill-ok', warn: 'pill-warn', bad: 'pill-bad',
    info: 'pill-info', mute: 'pill-mute'
  }[status.tone];

  return (
    <section className={`${accentClass} ${dense ? 'p-4' : 'p-5 sm:p-6'}`}>
      {(title || status) && (
        <div className="mb-4 flex items-center justify-between gap-3">
          {title && <h2 className="text-base font-semibold text-ink">{title}</h2>}
          {status && <span className={`pill ${pillToneClass}`}>{status.label}</span>}
        </div>
      )}
      <div>{children}</div>
    </section>
  );
}

/** A plain card without an accent stripe — used for groups that don't need
 *  the visual colored emphasis. */
export function Panel({ children, className = '' }: {
  children: React.ReactNode; className?: string;
}) {
  return <div className={`panel p-5 sm:p-6 ${className}`}>{children}</div>;
}

export function StatGrid({ children, cols }: {
  children: React.ReactNode;
  cols?: 3 | 4 | 5 | 6;
}) {
  // Auto-flow grid where the LAST row's cells don't stretch to full width
  // when the count doesn't divide evenly.
  const colClass = {
    3: 'sm:grid-cols-3',
    4: 'sm:grid-cols-2 md:grid-cols-4',
    5: 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5',
    6: 'sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-6'
  }[cols ?? 4];
  return <div className={`grid grid-cols-2 gap-3 ${colClass}`}>{children}</div>;
}

export function Stat({
  label, value, sub, tone = 'default'
}: {
  label: string;
  value: React.ReactNode;
  sub?: string;
  tone?: 'default' | 'ok' | 'warn' | 'bad' | 'info' | 'mute';
}) {
  const valueColor = {
    default: 'text-ink',
    ok:      'text-sev-ok',
    warn:    'text-sev-med',
    bad:     'text-sev-crit',
    info:    'text-accent',
    mute:    'text-ink-3'
  }[tone];
  return (
    <div className="stat min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">{label}</p>
      <p
        className={`tnum mt-1 text-lg font-bold leading-tight wrap-anywhere ${valueColor}`}
        style={{ hyphens: 'auto' }}
      >
        {value}
      </p>
      {sub && (
        <p className="mt-1 line-clamp-2 text-[10px] uppercase tracking-wider text-ink-3">
          {sub}
        </p>
      )}
    </div>
  );
}

export function Banner({
  tone = 'ok', title, children
}: {
  tone?: 'ok' | 'warn' | 'bad' | 'info';
  title: string;
  children?: React.ReactNode;
}) {
  // Tint + border + text are all derived from one severity token via
  // color-mix, so the banner reads correctly on paper and on ink.
  const sevVar = {
    ok: '--t-sev-ok',
    warn: '--t-sev-med',
    bad: '--t-sev-crit',
    info: '--t-sev-low'
  }[tone];
  return (
    <div
      className="rounded-lg border p-3.5 text-sm text-ink"
      style={{
        borderColor: `color-mix(in oklab, var(${sevVar}) 40%, transparent)`,
        background: `color-mix(in oklab, var(${sevVar}) 12%, transparent)`
      }}
    >
      <p className="font-semibold leading-tight" style={{ color: `color-mix(in oklab, var(${sevVar}) 75%, var(--t-ink))` }}>{title}</p>
      {children && <p className="mt-1 leading-relaxed text-ink-2">{children}</p>}
    </div>
  );
}

export function KV({ k, v }: { k: string; v: React.ReactNode }) {
  return (
    <div className="min-w-0">
      <p className="text-[10px] font-bold uppercase tracking-wider text-ink-3">{k}</p>
      <p className="mt-1 wrap-break-word text-sm font-medium text-ink">{v ?? '—'}</p>
    </div>
  );
}

/** A small tinted label chip, reusing the shared pill tones. */
export function Chip({ children, tone = 'mute' }: { children: React.ReactNode; tone?: Tone }) {
  const cls = { ok: 'pill-ok', warn: 'pill-warn', bad: 'pill-bad', info: 'pill-info', mute: 'pill-mute' }[tone];
  return <span className={`pill ${cls}`}>{children}</span>;
}

/** Monospace record block with a hover/focus copy button. Replaces bare
 *  <pre className="codeblock-light"> so raw records (SPF, DKIM, headers, …)
 *  are copyable and don't read as an undifferentiated developer dump. Long
 *  records scroll inside a bounded height; the copy always grabs the full text. */
export function CodeBlock({ children, label }: { children: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  const onCopy = () => {
    navigator.clipboard?.writeText(children)
      .then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); })
      .catch(() => { /* clipboard unavailable */ });
  };
  return (
    <div>
      {label && <p className="mb-1.5 text-[10px] font-bold uppercase tracking-wider text-ink-3">{label}</p>}
      <div className="group relative">
        <pre className="codeblock-light max-h-44 overflow-auto whitespace-pre-wrap break-all pr-11">{children}</pre>
        <button
          type="button"
          onClick={onCopy}
          aria-label={copied ? 'Copied' : 'Copy to clipboard'}
          title={copied ? 'Copied' : 'Copy'}
          className="no-print absolute right-1.5 top-1.5 grid h-8 w-8 place-items-center rounded-md border border-line-2 bg-panel/90 text-ink-3 opacity-0 backdrop-blur-sm transition hover:text-ink focus-visible:opacity-100 group-hover:opacity-100"
        >
          {copied ? <Check className="h-4 w-4 text-sev-ok" /> : <Copy className="h-4 w-4" />}
        </button>
      </div>
    </div>
  );
}

/** Backward-compat aliases used by older route files until they're rewritten. */
export const StatCardRow = StatGrid;
export const StatCard = Stat;
